import {
  Currency,
  LineType,
  OrderLine,
  OrderLinePublicType,
  OrderSummary,
  Payment,
  PaymentMethod,
  PaymentState,
} from "@smeetz/orders";
import {
  Discount,
  DiscountType,
  Voucher,
  VoucherType,
} from "@smeetz/order-common";
import type { IOfferV1 } from "@smeetz/offers-common";

type TranslateFn = (key: string, ...args: unknown[]) => string;

type OrderLineWithDiscounts = OrderLine & { discounts?: Discount[] };

export interface PendingVoucherRow {
  id: string;
  label: string;
  showPendingHint: boolean;
  amount: number;
  currency: string;
}

export interface PaymentCurrencyInfo {
  label: string;
  exchangeRate: number;
}

/**
 * Shared display logic for the order recap (lines, payments, vouchers), used
 * by both the cashier's AsideOrderSummary and the customer display basket so
 * the two screens can never drift apart.
 */

export function getOrderLineDisplayName(orderLine: OrderLine): string {
  // In case of a retail with only one option which is the default one we show category name
  return orderLine.itemType === OrderLinePublicType.RETAIL
    ? orderLine.retailName || orderLine.categoryName
    : orderLine.categoryName;
}

export function shouldShowTicketInfo(orderLine: OrderLine): boolean {
  // Show ticket product info (startDateTime, priceName ...etc) only if the product is not a merch product
  return orderLine.itemType !== OrderLinePublicType.RETAIL;
}

export function getLineStatusTranslation(
  orderLine: OrderLine,
  t: TranslateFn
): string | undefined {
  if (orderLine.itemType === OrderLinePublicType.RETAIL) {
    if (orderLine.lineType === LineType.SaleLine) {
      return t("common.confirmed-sets");
    }
    if (orderLine.lineType === LineType.CancelLine) {
      return t("common.cancelled-sets");
    }
  } else {
    if (orderLine.lineType === LineType.SaleLine) {
      return t("common.confirmed-tickets");
    }
    if (orderLine.lineType === LineType.CancelLine) {
      return t("common.cancelled-tickets");
    }
  }
  return undefined;
}

export function getOrderLineDiscountLines(
  orderLine: OrderLineWithDiscounts,
  orderSummary: OrderSummary,
  groupOffers: IOfferV1[],
  t: TranslateFn
): string[] {
  const lineDiscounts = (orderLine.discounts ?? []).filter(
    (d) => Number(d.value) > 0
  );
  if (!lineDiscounts.length) return [];

  const orderDiscounts = orderSummary.orderDiscounts;
  const curr = orderSummary.currency || Currency.CHF;

  const grouped = new Map<string, Discount[]>();
  for (const d of lineDiscounts) {
    const list = grouped.get(d.orderDiscountId) ?? [];
    list.push(d);
    grouped.set(d.orderDiscountId, list);
  }

  // Effective unit price for this line — prefer override if set.
  const unitPrice = Number(orderLine.overridePrice ?? orderLine.unitPrice ?? 0);
  const lines: string[] = [];
  for (const [odId, dList] of grouped) {
    const od = orderDiscounts.find((o) => o.id === odId);
    const total = dList.reduce((sum, d) => sum + Number(d.value || 0), 0);

    let label: string;
    if (od?.type === DiscountType.OFFER) {
      const offerDetails = groupOffers.find((o) => o.id === od.offerId);
      label = od.code
        ? String(od.code)
        : offerDetails?.name || t("label.offer-discount");
    } else {
      label = od?.code ? String(od.code) : t("discount.discount");
    }

    // Use `OrderDiscount.percentDiscount` when set (PERCENT and
    // percentage-based VOUCHER / MEMBERSHIP_VOUCHER / OFFER); otherwise
    // derive from `Discount.value / (qty * unitPrice)` for FLAT-style
    // discounts. Skip the derived form when it lands > 100% — the
    // per-unit base doesn't include addon contributions, so a >100%
    // figure would be misleading.
    let pct = 0;
    if (od?.percentDiscount && Number(od.percentDiscount) > 0) {
      pct = Math.round(Number(od.percentDiscount) * 100);
    } else {
      const appliedQty = dList.reduce(
        (sum, d) => sum + Number(d.quantityApplied || 0),
        0
      );
      const appliedBase = appliedQty * unitPrice;
      const derived =
        appliedBase > 0 ? Math.round((total / appliedBase) * 100) : 0;
      if (derived > 0 && derived <= 100) pct = derived;
    }
    if (pct > 0) {
      lines.push(`${label} ${pct}% / ${total.toFixed(2)} ${curr}`);
    } else {
      lines.push(`${label} ${total.toFixed(2)} ${curr}`);
    }
  }
  return lines;
}

export function getOrderLineDiscountedTotal(
  orderLine: OrderLineWithDiscounts
): number {
  const lineDiscountTotal = (orderLine.discounts ?? [])
    .filter((d) => Number(d.value) > 0)
    .reduce((sum, d) => sum + Number(d.value || 0), 0);
  return orderLine.totalPrice - lineDiscountTotal;
}

/**
 * Payments surfaced in the summary: every completed payment, plus pending
 * Cash and Swiss QR — the only two methods the cashier can still finalize or
 * undo from the summary screen.
 */
export function getDisplayedPayments(orderSummary: OrderSummary): Payment[] {
  return orderSummary.payments.filter((p) => {
    if (p.state === PaymentState.COMPLETED) return true;
    if (
      p.state === PaymentState.PENDING &&
      (p.paymentMethod === PaymentMethod.CASH ||
        p.paymentMethod === PaymentMethod.Swiss_QR_Code_Bank_Transfer)
    ) {
      return true;
    }
    return false;
  });
}

/**
 * Vouchers rendered as pending-payment rows at the bottom of the summary.
 * Label is derived from `voucherType` (Gift Card / Smeetz Voucher / Incert);
 * INCERT rows also show a "Pending Payment" subtitle since the redemption is
 * only finalized at order completion.
 */
export function getPendingVoucherRows(
  orderSummary: OrderSummary
): PendingVoucherRow[] {
  const curr = orderSummary.currency || Currency.CHF;
  return orderSummary.vouchers.map((voucher: Voucher) => {
    const code = String(voucher.code ?? "");
    let label: string;
    switch (voucher.voucherType) {
      case VoucherType.GIFT_CARD:
        label = "Gift card (POS)";
        break;
      case VoucherType.INCERT:
        label = `INCERT VOUCHER ${code}`.trim();
        break;
      case VoucherType.SMEETZ_VOUCHER:
      default:
        label = `Voucher ${code}`.trim();
    }
    return {
      id: voucher.id,
      label,
      showPendingHint: voucher.voucherType === VoucherType.INCERT,
      amount: Number(voucher.value || 0),
      currency: voucher.currency || curr,
    };
  });
}

/**
 * Distinct currencies used across the order's payments, with their exchange
 * rates — drives the extra per-currency total rows.
 */
export function getPaymentCurrencies(
  orderSummary: OrderSummary
): PaymentCurrencyInfo[] {
  const currencies = new Map<string, PaymentCurrencyInfo>();
  for (const payment of orderSummary.payments ?? []) {
    const currencyInfo = {
      label:
        payment.multiCurrencyInfo?.paymentCurrency ||
        payment.currency ||
        Currency.CHF,
      exchangeRate: payment.multiCurrencyInfo?.exchangeRate || 1,
    };

    if (!currencies.has(currencyInfo.label)) {
      currencies.set(currencyInfo.label, currencyInfo);
    }
  }
  return Array.from(currencies.values());
}
